import React, { useEffect, useState } from 'react';
import { Flex } from 'antd';
import { useQueryClient } from 'react-query';
import { useParams } from 'react-router-dom';

import { trackEvent } from 'jf/analytics/Analytics';
import { RosterClient, SlackTargetingClient, SlackUser } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { useJFOverlay } from 'jf/common/useJFOverlay';
import { DevExButton } from 'jf/components/DevExButton';
import { DevExModal } from 'jf/components/DevExModal';
import { useStudySlackTargetUpdater } from 'jf/pages/studyBuilder/targetRespondents/useStudySlackTargetUpdater';
import { useClientMutation, useClientQuery } from 'jf/utils/useClientQuery';

import { SlackTargetModalEditor } from './SlackTargetModalEditor';

// deprecating for MS Teams
export const ManageSlackTargetsModal: React.FC = () => {
  const modal = useJFOverlay(ManageSlackTargetsModal);
  const { studyRef } = useParams<{ studyRef: string; teamRef?: string }>();
  const theme = useDevExTheme();
  const studySlackTargetUpdater = useStudySlackTargetUpdater(studyRef);
  const queryClient = useQueryClient();

  const [unsavedSlackTargets, setUnsavedSlackTargets] = useState<SlackUser[]>([]);

  const [isSending, setIsSending] = useState(false);
  // retrieves respondents who have slack selected
  const { data: targetedSlackRespondents, refetch } = useClientQuery(
    RosterClient.getRosterTargets,
    {
      studyRef,
      slackSelected: true,
    }
  );

  const slackUsers = targetedSlackRespondents?.map((respondent) => respondent.slack!) || [];

  const { mutateAsync: sendStudyMessages } = useClientMutation(
    SlackTargetingClient.sendStudyMessages
  );

  const onSend = async () => {
    setIsSending(true);
    const targetsToSave = [...slackUsers, ...unsavedSlackTargets];
    if (unsavedSlackTargets.length) {
      await studySlackTargetUpdater.update(targetsToSave);
    }

    await sendStudyMessages({ studyRef: studyRef!, inviteOnly: true });
    setUnsavedSlackTargets([]);
    refetch();
    queryClient.invalidateQueries(['GET_STUDY', studyRef]);

    trackEvent(`survey-analyzer:manage-slack-targets:send`);

    setIsSending(false);
    modal.close();
  };

  useEffect(() => {
    setIsSending(false);
  }, [modal.isOpened]);

  return (
    <DevExModal
      open={modal.isOpened}
      onCancel={() => modal.close()}
      title="Manage recipients"
      width={550}
      footer={
        <DevExButton
          type="outline"
          disabled={isSending || !unsavedSlackTargets.length}
          onClick={() => onSend()}
        >
          Send to {unsavedSlackTargets.length} new receipient
          {unsavedSlackTargets.length === 1 ? '' : 's'}
        </DevExButton>
      }
    >
      <Flex vertical gap={theme.variable.spacing.md}>
        <div>
          Forget to send the survey to someone? Add more people here to invite them via Slack.
        </div>

        <SlackTargetModalEditor
          studyRef={studyRef!}
          unsavedSlackTargets={unsavedSlackTargets}
          onChange={setUnsavedSlackTargets}
        />
      </Flex>
    </DevExModal>
  );
};
